import type { CharacterControllerStateV1 } from "@gordonland/physics";
import type { RendererDiagnosticsSnapshotV1 } from "@gordonland/renderer";
import { WORLD_FORMAT_V1 } from "@gordonland/world-format";

const freeze = Object.freeze;
export const DIAGNOSTICS_UNSUPPORTED_ZH_CN_V1 = "不支持";

type DiagnosticFormatV1 = "boolean" | "bytes" | "integer" | "number-1" | "number-2" | "number-3" | "raw";

export interface DevelopmentDiagnosticMetricDescriptorV1 {
  readonly format: DiagnosticFormatV1;
  readonly group: "音频与环境" | "帧与渲染" | "物理与相机" | "网络与版本";
  readonly id: string;
  readonly label: string;
  readonly suffix?: string;
}

export const DEVELOPMENT_DIAGNOSTIC_METRICS_V1 = freeze([
  { id: "fps-instant", label: "即时 FPS", group: "帧与渲染", format: "number-1", suffix: " fps" },
  { id: "fps-rolling", label: "滚动 FPS", group: "帧与渲染", format: "number-1", suffix: " fps" },
  { id: "frame-ms-current", label: "当前帧时", group: "帧与渲染", format: "number-2", suffix: " ms" },
  { id: "frame-ms-average", label: "平均帧时", group: "帧与渲染", format: "number-2", suffix: " ms" },
  { id: "frame-ms-p95", label: "P95 帧时", group: "帧与渲染", format: "number-2", suffix: " ms" },
  { id: "frame-sample-state", label: "帧采样", group: "帧与渲染", format: "raw" },
  { id: "draw-calls", label: "Draw calls", group: "帧与渲染", format: "integer" },
  { id: "visible-triangles", label: "可见三角形", group: "帧与渲染", format: "integer" },
  { id: "chunks-active", label: "活动区块", group: "帧与渲染", format: "integer" },
  { id: "chunks-desired", label: "目标区块", group: "帧与渲染", format: "integer" },
  { id: "chunks-queued", label: "排队区块", group: "帧与渲染", format: "integer" },
  { id: "meshes-opaque", label: "不透明网格", group: "帧与渲染", format: "integer" },
  { id: "meshes-transparent", label: "透明网格", group: "帧与渲染", format: "integer" },
  { id: "worker-active", label: "Worker 活动作业", group: "帧与渲染", format: "integer" },
  { id: "worker-queue", label: "Worker 队列", group: "帧与渲染", format: "integer" },
  { id: "worker-p95-ms", label: "Worker 近期 P95", group: "帧与渲染", format: "number-2", suffix: " ms" },
  { id: "renderer-backend", label: "渲染后端", group: "帧与渲染", format: "raw" },
  { id: "quality-tier", label: "画质档位", group: "帧与渲染", format: "raw" },
  { id: "resolution-scale", label: "分辨率比例", group: "帧与渲染", format: "number-2", suffix: "×" },
  { id: "gpu-estimated-bytes", label: "GPU / 资源估算", group: "帧与渲染", format: "bytes" },
  { id: "retained-evicted-owners", label: "残留已淘汰资源", group: "帧与渲染", format: "integer" },
  { id: "js-heap-bytes", label: "JS 堆", group: "帧与渲染", format: "bytes" },
  { id: "character-grounded", label: "角色着地", group: "物理与相机", format: "boolean" },
  { id: "support-slope-degrees", label: "支撑坡度", group: "物理与相机", format: "number-1", suffix: "°" },
  { id: "vertical-speed", label: "垂直速度", group: "物理与相机", format: "number-2", suffix: " m/s" },
  { id: "penetration-depth", label: "当前穿透", group: "物理与相机", format: "number-3", suffix: " m" },
  { id: "maximum-penetration-depth", label: "最大穿透", group: "物理与相机", format: "number-3", suffix: " m" },
  { id: "physics-substeps", label: "本帧物理步数", group: "物理与相机", format: "integer" },
  { id: "physics-discarded-seconds", label: "丢弃积压时间", group: "物理与相机", format: "number-3", suffix: " s" },
  { id: "fixed-step-overflows", label: "固定步溢出", group: "物理与相机", format: "integer" },
  { id: "collision-recovery", label: "碰撞恢复", group: "物理与相机", format: "raw" },
  { id: "collision-recovery-failures", label: "碰撞恢复失败", group: "物理与相机", format: "integer" },
  { id: "collider-generation", label: "碰撞体代次", group: "物理与相机", format: "integer" },
  { id: "pinned-collision-owners", label: "固定碰撞体所有者", group: "物理与相机", format: "integer" },
  { id: "support-token", label: "支撑令牌", group: "物理与相机", format: "raw" },
  { id: "camera-yaw", label: "相机偏航", group: "物理与相机", format: "number-3", suffix: " rad" },
  { id: "camera-pitch", label: "相机俯仰", group: "物理与相机", format: "number-3", suffix: " rad" },
  { id: "camera-requested-arm", label: "请求镜臂", group: "物理与相机", format: "number-2", suffix: " m" },
  { id: "camera-resolved-arm", label: "实际镜臂", group: "物理与相机", format: "number-2", suffix: " m" },
  { id: "camera-obstructed", label: "相机受阻", group: "物理与相机", format: "boolean" },
  { id: "network-rtt-ms", label: "网络 RTT", group: "网络与版本", format: "number-1", suffix: " ms" },
  { id: "socket-state", label: "Socket 状态", group: "网络与版本", format: "raw" },
  { id: "network-buffered-bytes", label: "网络缓冲", group: "网络与版本", format: "bytes" },
  { id: "revision-applied", label: "已应用修订", group: "网络与版本", format: "raw" },
  { id: "revision-server", label: "服务端修订", group: "网络与版本", format: "raw" },
  { id: "revision-optimistic", label: "乐观修订", group: "网络与版本", format: "raw" },
  { id: "pending-operations", label: "待处理操作", group: "网络与版本", format: "integer" },
  { id: "replay-gaps", label: "重放缺口", group: "网络与版本", format: "integer" },
  { id: "build-id", label: "Build ID", group: "网络与版本", format: "raw" },
  { id: "protocol-version", label: "协议版本", group: "网络与版本", format: "raw" },
  { id: "world-format-version", label: "世界格式版本", group: "网络与版本", format: "integer" },
  { id: "generator-version", label: "生成器版本", group: "网络与版本", format: "integer" },
  { id: "catalog-version", label: "目录版本", group: "网络与版本", format: "integer" },
  { id: "environment-version", label: "环境版本", group: "音频与环境", format: "integer" },
  { id: "game-time", label: "游戏时间", group: "音频与环境", format: "raw" },
  { id: "weather", label: "天气", group: "音频与环境", format: "raw" },
  { id: "weather-mode", label: "天气模式", group: "音频与环境", format: "raw" },
  { id: "hash-provider", label: "哈希提供者", group: "音频与环境", format: "raw" },
  { id: "audio-context-state", label: "音频上下文", group: "音频与环境", format: "raw" },
  { id: "audio-voices", label: "音频声部", group: "音频与环境", format: "integer" },
  { id: "audio-decoded-bytes", label: "音频解码内存", group: "音频与环境", format: "bytes" },
] as const satisfies readonly DevelopmentDiagnosticMetricDescriptorV1[]);

export type DevelopmentDiagnosticMetricIdV1 = (typeof DEVELOPMENT_DIAGNOSTIC_METRICS_V1)[number]["id"];
export type DevelopmentDiagnosticRawValueV1 = boolean | number | string | null;
export type DevelopmentDiagnosticValuesV1 = Readonly<Record<DevelopmentDiagnosticMetricIdV1, DevelopmentDiagnosticRawValueV1>>;

export interface DevelopmentDiagnosticsSnapshotV1 {
  readonly sampledAtMonotonicMs: number;
  readonly values: DevelopmentDiagnosticValuesV1;
}

export interface DevelopmentDiagnosticsLiveInputV1 {
  readonly buildId: string | null;
  readonly character: CharacterControllerStateV1;
  readonly colliderGeneration: number;
  readonly collisionRecoveryFailures: number;
  readonly documentHidden: boolean;
  readonly fixedStepOverflowCount: number;
  readonly jsHeapBytes: number | null;
  readonly lastAppliedRevision: string;
  readonly maximumPenetrationDepthMetres: number;
  readonly physicsDiscardedSeconds: number;
  readonly physicsSubsteps: number;
  readonly renderer: RendererDiagnosticsSnapshotV1;
  readonly worker: {
    readonly activeJobs: number;
    readonly queueLength: number;
    readonly recentP95DurationMs: number | null;
  };
}

function rendererValue(value: number | "unsupported"): number | null {
  return value === "unsupported" ? null : value;
}

export function createLiveDevelopmentDiagnosticsSnapshotV1(
  input: DevelopmentDiagnosticsLiveInputV1,
): DevelopmentDiagnosticsSnapshotV1 {
  const character = input.character;
  const renderer = input.renderer;
  const supportSlope = character.grounded ? character.supportSlopeDegrees : "无支撑";
  const values = freeze({
    "fps-instant": renderer.fpsInstant,
    "fps-rolling": renderer.fpsRolling,
    "frame-ms-current": renderer.frameMsCurrent,
    "frame-ms-average": renderer.frameMsAverage,
    "frame-ms-p95": renderer.frameMsP95,
    "frame-sample-state": input.documentHidden ? "后台样本已排除" : "前台采样中",
    "draw-calls": rendererValue(renderer.drawCalls),
    "visible-triangles": rendererValue(renderer.visibleTriangles),
    "chunks-active": renderer.activeChunks,
    "chunks-desired": renderer.desiredChunks,
    "chunks-queued": input.worker.queueLength,
    "meshes-opaque": renderer.opaqueMeshes,
    "meshes-transparent": renderer.transparentMeshes,
    "worker-active": input.worker.activeJobs,
    "worker-queue": input.worker.queueLength,
    "worker-p95-ms": input.worker.recentP95DurationMs,
    "renderer-backend": renderer.backend,
    "quality-tier": renderer.qualityTier,
    "resolution-scale": renderer.resolutionScale,
    "gpu-estimated-bytes": renderer.estimatedGpuBytes,
    "retained-evicted-owners": renderer.retainedEvictedOwners,
    "js-heap-bytes": input.jsHeapBytes,
    "character-grounded": character.grounded,
    "support-slope-degrees": supportSlope,
    "vertical-speed": character.velocityMetresPerSecond.y,
    "penetration-depth": character.penetrationDepthMetres,
    "maximum-penetration-depth": input.maximumPenetrationDepthMetres,
    "physics-substeps": input.physicsSubsteps,
    "physics-discarded-seconds": input.physicsDiscardedSeconds,
    "fixed-step-overflows": input.fixedStepOverflowCount,
    "collision-recovery": character.collisionRecovery.kind,
    "collision-recovery-failures": input.collisionRecoveryFailures,
    "collider-generation": input.colliderGeneration,
    "pinned-collision-owners": character.supportColliderToken === null ? 0 : 1,
    "support-token": character.supportColliderToken === null ? "无" : "已绑定",
    "camera-yaw": renderer.cameraYawRadians,
    "camera-pitch": renderer.cameraPitchRadians,
    "camera-requested-arm": renderer.cameraRequestedArmMetres,
    "camera-resolved-arm": renderer.cameraResolvedArmMetres,
    "camera-obstructed": renderer.cameraObstructed,
    "network-rtt-ms": null,
    "socket-state": null,
    "network-buffered-bytes": null,
    "revision-applied": input.lastAppliedRevision,
    "revision-server": null,
    "revision-optimistic": null,
    "pending-operations": null,
    "replay-gaps": null,
    "build-id": input.buildId,
    // Frozen Phase 0 display contract: PROTOCOL_V1 major 1, maximum minor 1.
    "protocol-version": "1.1",
    "world-format-version": WORLD_FORMAT_V1.worldFormatVersion,
    "generator-version": WORLD_FORMAT_V1.generatorVersion,
    "catalog-version": WORLD_FORMAT_V1.blockCatalogVersion,
    "environment-version": null,
    "game-time": null,
    weather: null,
    "weather-mode": null,
    "hash-provider": "Web Crypto SHA-256",
    "audio-context-state": null,
    "audio-voices": null,
    "audio-decoded-bytes": null,
  } satisfies DevelopmentDiagnosticValuesV1);
  return freeze({ sampledAtMonotonicMs: performance.now(), values });
}

const metricIds = new Set<string>(DEVELOPMENT_DIAGNOSTIC_METRICS_V1.map(({ id }) => id));

function validProbeValue(value: unknown): value is DevelopmentDiagnosticRawValueV1 {
  return value === null || typeof value === "boolean" ||
    typeof value === "number" && Number.isFinite(value) ||
    typeof value === "string" && value.length <= 64 &&
      [...value].every((character) => character.codePointAt(0) !== 127 && (character.codePointAt(0) ?? 0) >= 32);
}

export function mergeDevelopmentDiagnosticsProbeV1(
  base: DevelopmentDiagnosticsSnapshotV1,
  overrides: Readonly<Record<string, unknown>>,
): DevelopmentDiagnosticsSnapshotV1 {
  const next: Record<string, DevelopmentDiagnosticRawValueV1> = { ...base.values };
  for (const [id, value] of Object.entries(overrides)) {
    if (!metricIds.has(id)) throw new TypeError(`未知诊断指标：${id}`);
    if (!validProbeValue(value)) throw new TypeError(`诊断指标值无效：${id}`);
    next[id] = value;
  }
  return freeze({
    sampledAtMonotonicMs: performance.now(),
    values: freeze(next) as DevelopmentDiagnosticValuesV1,
  });
}

function bytes(value: number): string {
  if (value < 1_024) return `${value.toFixed(0)} B`;
  if (value < 1_048_576) return `${(value / 1_024).toFixed(1)} KiB`;
  return `${(value / 1_048_576).toFixed(1)} MiB`;
}

export function formatDevelopmentDiagnosticValueV1(
  descriptor: DevelopmentDiagnosticMetricDescriptorV1,
  value: DevelopmentDiagnosticRawValueV1,
): string {
  if (value === null) return DIAGNOSTICS_UNSUPPORTED_ZH_CN_V1;
  if (descriptor.format === "boolean") return value === true ? "是" : value === false ? "否" : String(value);
  if (typeof value !== "number") return String(value);
  if (descriptor.format === "bytes") return bytes(value);
  if (descriptor.format === "integer") return Math.round(value).toLocaleString("zh-CN");
  if (descriptor.format === "number-1") return `${value.toFixed(1)}${descriptor.suffix ?? ""}`;
  if (descriptor.format === "number-2") return `${value.toFixed(2)}${descriptor.suffix ?? ""}`;
  if (descriptor.format === "number-3") return `${value.toFixed(3)}${descriptor.suffix ?? ""}`;
  return String(value);
}
